// The eval runners (10 DC-02: "eval runner (fixture and replay kinds; `live` behind a flag)";
// 01 "Eval": a reproducible check that fails before a skill edit and passes after it). Both
// kinds are deterministic: no model is called, so the same version and the same stored inputs
// always give the same result, and a drift audit re-run means something.
//
// - `fixture`: `spec_ref` names a stored fixture (`companyos.eval-fixture/v1`), a list of checks
//   over the version's own content: text a target must or must not contain, skills present or
//   absent in the index, a rule with an enforcer at least as strong as named, a role's model.
//   It passes when every check holds.
// - `replay`: `spec_ref` names a replay spec (`companyos.eval-replay/v1`) whose stored run
//   decisions (02 `run_decision`, read per company through the agency grant; default: the
//   eval's `proves_mistake_ref`) are replayed against the version's rule → enforcer table. Each
//   failed decision must be covered by a rule proven against it with an enforcer at least as
//   strong as the spec asks, plus the spec's own checks. A decision that cannot be read, or a
//   reference with no failed decision, is `error`: there is no mistake to replay.
// - `live`: no runner yet; `runEvals` runs a `live` eval only with `allowLive` (the flag), and
//   without a live runner the result is `error`.
//
// Loaders are injected (the API reads the object store and the cell). Anything a loader returns
// is untrusted and parsed strictly; a loader that throws makes the eval `error`.
import { z } from "zod";
import { canonicalJson } from "../canonical";
import {
  ENFORCER_RANK,
  type EnforcerKind,
  enforcerKindSchema,
  type MistakeRef,
  mistakeRefSchema,
  type TemplateEval,
  type TemplateVersion,
} from "../schemas";
import type { EvalRunner, EvalRunResult } from "./evals";
import { renderSkillIndex } from "./token-counter";

export const FIXTURE_FORMAT = "companyos.eval-fixture/v1";
export const REPLAY_FORMAT = "companyos.eval-replay/v1";
/** Bounds on stored specs (untrusted input). */
export const MAX_EVAL_CHECKS = 200;
export const MAX_REPLAY_DECISIONS = 50;
const MAX_TEXT = 4096;

const TARGETS = [
  "standing_orders",
  "skill_index",
  "rules",
  "tool_grants",
  "runtime_config",
  "role_model_map",
] as const;
const text = z.string().min(1).max(MAX_TEXT);

export const evalCheckSchema = z.discriminatedUnion("op", [
  z.strictObject({ op: z.literal("contains"), target: z.enum(TARGETS), text }),
  z.strictObject({ op: z.literal("excludes"), target: z.enum(TARGETS), text }),
  z.strictObject({ op: z.literal("has_skill"), skill: text }),
  z.strictObject({ op: z.literal("lacks_skill"), skill: text }),
  z.strictObject({ op: z.literal("has_rule"), key: text, at_least: enforcerKindSchema.optional() }),
  z.strictObject({ op: z.literal("role_model"), role: text, model: text }),
]);
export type EvalCheck = z.infer<typeof evalCheckSchema>;

export const fixtureSpecSchema = z.strictObject({
  format: z.literal(FIXTURE_FORMAT),
  checks: z.array(evalCheckSchema).min(1).max(MAX_EVAL_CHECKS),
});
export type FixtureSpec = z.infer<typeof fixtureSpecSchema>;

export const replaySpecSchema = z.strictObject({
  format: z.literal(REPLAY_FORMAT),
  /** Defaults to the eval's `provesMistakeRef`. */
  decisions: z.array(mistakeRefSchema).min(1).max(MAX_REPLAY_DECISIONS).optional(),
  /** The weakest enforcer that counts as covering a mistake; default any (`written_rule`). */
  require_enforcer_at_least: enforcerKindSchema.optional(),
  checks: z.array(evalCheckSchema).max(MAX_EVAL_CHECKS).default([]),
});
export type ReplaySpec = z.infer<typeof replaySpecSchema>;

/** A stored run decision as the replay needs it (02 `run_decision`). */
export const replayDecisionSchema = z.strictObject({
  companyId: z.string().min(1),
  runId: z.string().min(1),
  id: z.string().min(1),
  result: z.enum(["ok", "failed", "unknown"]),
});
export type ReplayDecision = z.infer<typeof replayDecisionSchema>;

export type EvalLoaders = {
  /** The stored spec at `spec_ref` (parsed JSON), or null when there is none. */
  loadSpec(specRef: string): Promise<unknown>;
  /**
   * The decisions a mistake reference names: the one decision when `runDecisionId` is set,
   * otherwise every decision of the run. Null when the company or run cannot be read.
   */
  loadDecisions(ref: MistakeRef): Promise<unknown[] | null>;
};

const targetText = (v: TemplateVersion, target: (typeof TARGETS)[number]): string => {
  switch (target) {
    case "standing_orders":
      return v.standingOrders;
    case "skill_index":
      return renderSkillIndex(v.skillIndex);
    case "rules":
      return canonicalJson(v.rules);
    case "tool_grants":
      return canonicalJson(v.toolGrants);
    case "runtime_config":
      return canonicalJson(v.runtimeConfig);
    case "role_model_map":
      return canonicalJson(v.roleModelMap);
  }
};

const atLeast = (kind: EnforcerKind, floor: EnforcerKind | undefined) =>
  ENFORCER_RANK[kind] <= ENFORCER_RANK[floor ?? "written_rule"];

/** Which checks fail on `version`, as `index: reason` strings. Empty when every check holds. */
export function failedChecks(version: TemplateVersion, checks: readonly EvalCheck[]): string[] {
  const failed: string[] = [];
  checks.forEach((c, i) => {
    let ok: boolean;
    switch (c.op) {
      case "contains":
        ok = targetText(version, c.target).includes(c.text);
        break;
      case "excludes":
        ok = !targetText(version, c.target).includes(c.text);
        break;
      case "has_skill":
        ok = version.skillIndex.some((e) => e.skill === c.skill);
        break;
      case "lacks_skill":
        ok = !version.skillIndex.some((e) => e.skill === c.skill);
        break;
      case "has_rule":
        ok = version.rules.some((r) => r.key === c.key && atLeast(r.enforcerKind, c.at_least));
        break;
      case "role_model":
        ok = Object.hasOwn(version.roleModelMap, c.role)
          ? version.roleModelMap[c.role]?.model === c.model
          : false;
        break;
    }
    if (!ok) failed.push(`${i}: ${c.op}`);
  });
  return failed;
}

/** True when `rule` was proven against this decision (same company and run; same decision if named). */
const provenAgainst = (ref: MistakeRef, d: ReplayDecision) =>
  ref.companyId === d.companyId &&
  ref.runId === d.runId &&
  (ref.runDecisionId === undefined || ref.runDecisionId === d.id);

/** Runs one fixture eval. Throws on unreadable or malformed input (the caller maps it to `error`). */
export async function runFixtureEval(
  loaders: Pick<EvalLoaders, "loadSpec">,
  e: TemplateEval,
  version: TemplateVersion,
): Promise<EvalRunResult> {
  const spec = fixtureSpecSchema.parse(await loaders.loadSpec(e.specRef));
  const failed = failedChecks(version, spec.checks);
  return failed.length === 0
    ? { result: "pass", evidenceRef: `fixture:${spec.checks.length} checks` }
    : { result: "fail", evidenceRef: `fixture:failed ${failed.join(", ")}` };
}

/** Runs one replay eval. Throws when a decision cannot be read or nothing is a failure. */
export async function runReplayEval(
  loaders: EvalLoaders,
  e: TemplateEval,
  version: TemplateVersion,
): Promise<EvalRunResult> {
  const spec = replaySpecSchema.parse(await loaders.loadSpec(e.specRef));
  const refs = spec.decisions ?? (e.provesMistakeRef ? [e.provesMistakeRef] : []);
  if (refs.length === 0) throw new Error("replay names no decisions and the eval no mistake");
  const failures: ReplayDecision[] = [];
  for (const ref of refs) {
    const raw = await loaders.loadDecisions(ref);
    if (raw === null) throw new Error("a referenced run cannot be read");
    const decisions = z.array(replayDecisionSchema).max(1000).parse(raw);
    const named = decisions.filter(
      (d) =>
        d.companyId === ref.companyId &&
        d.runId === ref.runId &&
        (ref.runDecisionId === undefined || d.id === ref.runDecisionId),
    );
    if (named.length === 0) throw new Error("a referenced decision does not exist");
    failures.push(...named.filter((d) => d.result === "failed"));
  }
  if (failures.length === 0) throw new Error("no referenced decision is a failure");
  const floor = spec.require_enforcer_at_least;
  const uncovered = failures.filter(
    (d) =>
      !version.rules.some(
        (r) => provenAgainst(r.provenAgainstRef, d) && atLeast(r.enforcerKind, floor),
      ),
  );
  const failed = failedChecks(version, spec.checks);
  return uncovered.length === 0 && failed.length === 0
    ? { result: "pass", evidenceRef: `replay:${failures.length} decisions covered` }
    : {
        result: "fail",
        evidenceRef: `replay:${uncovered.length} of ${failures.length} uncovered; checks failed ${failed.length}`,
      };
}

export const EVAL_RUNNER_NAME = "companyos-evals/v1";

/**
 * The runner `runEvals` takes: fixture and replay through `loaders`, `live` through `live` when
 * given (and only when `runEvals` is called with `allowLive`). A kind without a runner throws,
 * which `runEvals` records as `error`.
 */
export function createEvalRunner(loaders: EvalLoaders, live?: EvalRunner): EvalRunner {
  return {
    name: live ? `${EVAL_RUNNER_NAME}+${live.name}` : EVAL_RUNNER_NAME,
    async run(input) {
      switch (input.eval.kind) {
        case "fixture":
          return runFixtureEval(loaders, input.eval, input.version);
        case "replay":
          return runReplayEval(loaders, input.eval, input.version);
        case "live":
          if (!live) throw new Error("no live eval runner");
          return live.run(input);
      }
    },
  };
}
